import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, IconButton, Typography } from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";

const testimonials = [
  {
    id: 1,
    name: "Priya Sharma",
    role: "Working Professional",
    initials: "PS",
    rating: 5,
    text:
      "Rite Wellness transformed how I manage my health. Having 24/7 access to my doctor gives me peace of mind. The app is incredibly intuitive, and the care team is always responsive."
  },
  {
    id: 2,
    name: "Rajesh Kumar",
    role: "Business Owner",
    initials: "RK",
    rating: 5,
    text:
      "After struggling with diabetes management for years, Rite Wellness's personalized approach made all the difference. My HbA1c levels have never been better."
  },
  {
    id: 3,
    name: "Anita Patel",
    role: "Mother of Two",
    initials: "AP",
    rating: 5,
    text:
      "The family membership is incredible value. From my parents' chronic care to my kids' vaccinations, everything is handled seamlessly. Truly a one-stop wellness solution."
  },
  {
    id: 4,
    name: "Vikram Singh",
    role: "Senior Executive",
    initials: "VS",
    rating: 5,
    text:
      "The mental health support has been life-changing. Having a therapist available when I need them, plus the stress management programs, helped me regain work-life balance."
  },
  {
    id: 5,
    name: "Meera Reddy",
    role: "Fitness Enthusiast",
    initials: "MR",
    rating: 5,
    text:
      "The nutrition planning and weight management program exceeded my expectations. Lost 15 kg in 6 months with medical supervision. The dietitians are phenomenal!"
  }
];

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const next = () => setCurrentIndex((p) => (p + 1) % testimonials.length);
  const prev = () => setCurrentIndex((p) => (p - 1 + testimonials.length) % testimonials.length);

  const t = testimonials[currentIndex];

  return (
    <Box component="section" className="section-padding wellness-bg" sx={{ position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", top: 40, left: 40, opacity: 0.1 }}>
        <FormatQuoteRoundedIcon sx={{ fontSize: 160, color: "primary.main" }} />
      </Box>

      <Box className="container-custom" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "rgba(39,165,153,0.1)",
              color: "primary.main",
              fontSize: 14,
              fontWeight: 600,
              mb: 2
            }}
          >
            Testimonials
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.15
            }}
          >
            Trusted by Thousands
            <Box component="span" className="gradient-text" sx={{ display: "block" }}>
              of Happy Patients
            </Box>
          </Typography>

          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            Real stories from real people who transformed their health with Rite Wellness.
          </Typography>
        </motion.div>

        <Box sx={{ maxWidth: 896, mx: "auto" }}>
          <Box sx={{ position: "relative" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
              >
                <Box className="glass-card" sx={{ p: { xs: 4, md: 6 } }}>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start", gap: 4 }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          bgcolor: "rgba(39,165,153,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 24,
                          fontWeight: 800,
                          color: "primary.main"
                        }}
                      >
                        {t.initials}
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <StarRoundedIcon key={i} sx={{ color: "#fbbf24" }} />
                        ))}
                      </Box>

                      <Typography
                        component="blockquote"
                        sx={{
                          fontSize: { xs: "1.125rem", md: "1.25rem" },
                          color: "text.primary",
                          mb: 3,
                          lineHeight: 1.7
                        }}
                      >
                        "{t.text}"
                      </Typography>

                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{t.role}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </AnimatePresence>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 4 }}>
              <IconButton
                onClick={prev}
                aria-label="Previous testimonial"
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "background.paper",
                  border: "1px solid hsl(var(--border))",
                  "&:hover": { bgcolor: "hsl(var(--muted))" }
                }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>

              <Box sx={{ display: "flex", gap: 1 }}>
                {testimonials.map((_, idx) => (
                  <Box
                    key={idx}
                    component="button"
                    onClick={() => setCurrentIndex(idx)}
                    aria-label={`Go to testimonial ${idx + 1}`}
                    sx={{
                      border: "none",
                      cursor: "pointer",
                      height: 10,
                      borderRadius: 999,
                      width: idx === currentIndex ? 32 : 10,
                      backgroundColor:
                        idx === currentIndex
                          ? "#27a599"
                          : "rgba(39,165,153,0.3)",
                      transition: "all 300ms ease"
                    }}
                  />
                ))}
              </Box>

              <IconButton
                onClick={next}
                aria-label="Next testimonial"
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "background.paper",
                  border: "1px solid hsl(var(--border))",
                  "&:hover": { bgcolor: "hsl(var(--muted))" }
                }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
